import React, { useContext } from 'react';
import styled from 'styled-components';
import get from 'lodash/get';
import {
  cta2FontWeight,
  screenMdMin,
  screenSmMax,
  screenXsMax,
  spacer16,
  spacer24,
  spacer32,
  spacer48,
  spacer8,
  Type,
  makeColorSetClass,
  cssColorValue,
  KeyboardDetectionContext,
} from '@spotify-internal/encore-web-v5';
import {
  DELAYS,
  fadeInAnimation,
  Grid,
  withAnimationContext,
} from '@spotify-internal/frodor-modules';
import { Link, CTAButtonType, CTAButton } from '../../ui';
import { formatText, getBurst, transformScaleUp } from '../../utils';
import androidIcon from '../../Layout/images/footer-android.svg';
import iosIcon from '../../Layout/images/footer-ios.svg';
import { useT } from '@spotify-internal/i18n-core';
import { ModuleType } from '../types';
import { MODULE_COLOR_SETS } from '../../Theme';

const TRACK_CAT = 'module-pageBreaker';

const PageBreaker = styled(Grid)`
  display: flex;
  align-items: center;
  min-height: 236px;
  padding-top: ${spacer24};
  padding-bottom: ${spacer24};

  ${(props: { showBurst: boolean; theme: any }) =>
    props.showBurst ? getBurst(props.theme.box.text) : ''};

  @media (max-width: ${screenSmMax}) {
    flex-direction: column;
    justify-content: center;
    min-height: auto;
    background-image: none;
  }
`;

const Left = styled.div`
  width: 100%;
  padding-top: ${spacer16};
  padding-bottom: ${spacer32};
  @media (max-width: ${screenSmMax}) {
    text-align: left;
  }
`;

const Right = styled.div`
  width: 100%;
  padding-top: ${spacer32};
  padding-bottom: ${spacer32};
  @media (max-width: ${screenSmMax}) {
    padding-top: 0px;
    padding-bottom: ${spacer16};
    align-items: center;
  }
`;

const Headline = withAnimationContext(
  styled(Type.h1).attrs({
    paddingBottom: '0.67em',
    variant: 'altoBrio',
  })`
    ${fadeInAnimation(DELAYS.SECOND)};
    font-weight: ${cta2FontWeight};
    padding-bottom: 0;
  `,
);

const SubtextContainer = withAnimationContext(
  styled.div.attrs({
    variant: 'ballad',
  })`
    ${fadeInAnimation(DELAYS.THIRD)};
    padding-top: ${spacer8};
    padding-bottom: ${spacer8};
    @media (max-width: ${screenSmMax}) {
      padding-bottom: 0;
    }
  `,
);

const AppStoreLink = styled(Link)<{ isUsingKeyboard?: boolean }>`
  :focus:not(:disabled) {
    img {
      ${transformScaleUp};
      outline: 3px solid;
      outline-color: ${({ isUsingKeyboard }) =>
        isUsingKeyboard ? cssColorValue('textBase') : 'transparent'};
      outline-offset: 3px;
      border-radius: 3px;
    }
  }
`;

const CTAContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-right: ${spacer48};

  @media (max-width: ${screenSmMax}) {
    justify-content: initial;
  }

  @media (max-width: ${screenXsMax}) {
    padding-top: 0px;
    padding-bottom: ${spacer24};
    padding-right: 0;
    justify-content: center;
  }
`;

const AppStores: React.FC = () => {
  const t = useT();
  const { isUsingKeyboard } = useContext(KeyboardDetectionContext);
  return (
    <>
      <AppStoreLink
        external
        href="https://itunes.apple.com/us/app/spotify-for-artists/id1222021797"
        track={[TRACK_CAT, `CTA App Store Link - iOS`]}
        isUsingKeyboard={isUsingKeyboard}
      >
        <img
          src={iosIcon}
          alt={t(
            'FRODOR_147246',
            'Download banner for iOS',
            'Banner refers to the visual element on the page',
          )}
        />
      </AppStoreLink>
      <AppStoreLink
        external
        href="https://play.google.com/store/apps/details?id=com.spotify.s4a"
        track={[TRACK_CAT, `CTA App Store Link - Android`]}
        isUsingKeyboard={isUsingKeyboard}
      >
        <img
          src={androidIcon}
          alt={t(
            'FRODOR_6a8897',
            'Download banner for Android',
            'Banner refers to the visual element on the page',
          )}
        />
      </AppStoreLink>
    </>
  );
};

const AppStoresContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  @media (max-width: ${screenSmMax}) {
    padding-top: ${spacer16};
    padding-bottom: ${spacer16};
    padding-left: ${spacer16};
    justify-content: flex-start;
  }
  @media (min-width: ${screenMdMin}) {
    a:first-child {
      margin-right: ${spacer24};
      margin-left: ${spacer24};
    }
  }
  @media (max-width: ${screenSmMax}) {
    a:first-child {
      margin-right: ${spacer8};
    }
  }
`;

const getPageBreakerVersion = (
  moduleVersion: string,
  ctaButton: CTAButtonType,
  themeName: string,
) => {
  switch (moduleVersion) {
    case 'Call to Action':
      if (
        get(ctaButton, 'ctaText.length') > 0 &&
        get(ctaButton, 'ctaLink.length') > 0
      ) {
        return (
          <CTAContainer>
            <CTAButton
              {...ctaButton}
              themeName={themeName}
              trackingCategory={TRACK_CAT}
            />
          </CTAContainer>
        );
      }
      return <></>;
    case 'App Stores':
      return (
        <AppStoresContainer>
          <AppStores />
        </AppStoresContainer>
      );
    default:
      return <></>;
  }
};

export type PageBreakerModuleType = ModuleType & {
  headline?: string;
  subtext?: string;
  ctaButton?: CTAButtonType;
  moduleVersion?: string;
  showBurst?: boolean;
};

export const PageBreakerModule = React.forwardRef<
  React.MutableRefObject<ModuleType>,
  PageBreakerModuleType
>(
  (
    {
      headline = '',
      subtext = '',
      ctaButton = {
        ctaText: '',
        ctaLink: '',
        title: '',
      } as CTAButtonType,
      moduleVersion = 'Call to Action',
      showBurst = false,
      themeName = '',
    },
    ref,
  ) => {
    const colorSetClass = makeColorSetClass(MODULE_COLOR_SETS[themeName]);
    return (
      <PageBreaker className={colorSetClass} ref={ref} showBurst={showBurst}>
        <Left>
          {headline && (
            <Headline
              dangerouslySetInnerHTML={{ __html: formatText(headline) }}
            />
          )}
          {subtext && (
            <SubtextContainer
              dangerouslySetInnerHTML={{ __html: formatText(subtext) }}
            />
          )}
        </Left>
        <Right>
          {getPageBreakerVersion(moduleVersion, ctaButton, themeName)}
        </Right>
      </PageBreaker>
    );
  },
);
